import { z } from 'zod';
import {
  CompetitorEngagementAttributionModelOutput,
  CompetitorHotspotPatternsModelOutput,
  DailyRecommendationEvidence,
  DailyRecommendationModelItemOutput,
  NoteInsightModelOutput,
  SelfNoteAnalysisContentFeaturesModelOutput,
  SelfNoteAnalysisFindingModelOutput,
  SelfNoteAnalysisImprovementModelOutput,
  TOPIC_RECOMMENDATION_COUNT,
  TopicCompetitorReferenceModelOutput,
  TopicOpportunityModelOutput,
  TopicRecommendationModelOutput,
  TopicSkillHintModelOutput,
  type AnalysisTaskType,
} from './analysis';
import { GeneratedDraftModelOutput } from './content';
import { Platform } from './platform';

export const MODEL_AI_TASK_TYPES = [
  'self_note_analysis',
  'competitor_insight',
  'skill_extraction',
  'topic_recommend',
  'daily_recommendation',
  'aigc_generation',
  'aigc_rewrite',
] as const satisfies readonly AnalysisTaskType[];

export const DETERMINISTIC_AI_TASK_TYPES = [
  'content_safety',
] as const satisfies readonly AnalysisTaskType[];

export const PLACEHOLDER_AI_TASK_TYPES = [
  'video_transcription',
  'video_scene_detect',
  'video_ocr',
  'video_summarize',
  'cross_platform_compare',
] as const satisfies readonly AnalysisTaskType[];

export type ModelAiTaskType = (typeof MODEL_AI_TASK_TYPES)[number];
export type DeterministicAiTaskType = (typeof DETERMINISTIC_AI_TASK_TYPES)[number];
export type PlaceholderAiTaskType = (typeof PLACEHOLDER_AI_TASK_TYPES)[number];

export const AI_FEATURE_KEYS = [
  'self_note_analysis',
  'competitor_insight',
  'skill_extraction',
  'topic_recommend',
  'daily_recommendation',
  'aigc_generation',
] as const;

export type AiFeatureKey = (typeof AI_FEATURE_KEYS)[number];
export type AiTaskKind = 'model' | 'deterministic' | 'placeholder';
export type StructuredOutputRequirement = 'required' | 'preferred' | 'disabled';

export interface AiTaskContractDefinition<
  TInputSchema extends z.ZodType = z.ZodType,
  TOutputSchema extends z.ZodType = z.ZodType,
> {
  taskType: ModelAiTaskType;
  featureKey: AiFeatureKey;
  taskDisplayName: string;
  prompt: {
    required: true;
    platformStrategy: 'platform_then_default';
  };
  inputContract: {
    version: string;
    schema: TInputSchema;
    jsonSchema: Record<string, unknown>;
    example: z.input<TInputSchema>;
  };
  outputContract: {
    version: string;
    schema: TOutputSchema;
    jsonSchema: Record<string, unknown>;
    example: z.input<TOutputSchema>;
  };
  modelRequirements: {
    structuredOutput: StructuredOutputRequirement;
    vision: boolean;
  };
  inference: {
    maxTokens: number;
    temperature: number;
  };
}

export interface AiTaskClassification {
  kind: AiTaskKind;
  featureKey: AiFeatureKey | null;
  promptRequired: boolean;
}

const NullableText = z.string().trim().min(1).nullable();
const JsonValue = z.json();

const NoteModelInput = z
  .object({
    platform: Platform,
    account_id: z.string().trim().min(1).nullable(),
    author: z
      .object({
        external_id: z.string().trim().min(1).nullable(),
        display_name: z.string().trim().min(1).nullable(),
      })
      .strict(),
    content_type: z.string().trim().min(1).max(80),
    title: NullableText,
    summary: NullableText,
    body: z.string().max(4000).nullable(),
    body_truncated: z.boolean(),
    tags: z.array(z.string().trim().min(1).max(80)).max(50),
    engagement: z.record(z.string(), z.number()),
    media_count: z.number().int().nonnegative(),
    source_url: z.string().url().nullable(),
    published_at: z.string().trim().min(1).nullable(),
  })
  .strict();

const SkillExtractionSourceNoteInput = z
  .object({
    content_type: z.string().trim().min(1).max(80),
    media_count: z.number().int().nonnegative(),
    tags: z.array(z.string().trim().min(1).max(80)).max(50),
    engagement: z.record(z.string(), JsonValue),
    analysis_score: z.number().min(0).max(100).nullable(),
    analysis_originality: z.number().min(0).max(100).nullable(),
    performance_score: z.number().min(0),
    published_at: z.string().trim().min(1).nullable(),
    title: NullableText,
    summary: NullableText,
    body_excerpt: z.string().max(1200),
    body_truncated: z.boolean(),
  })
  .strict();

const SkillExtractionInput = z
  .object({
    platform: Platform,
    account_id: z.string().trim().min(1),
    source_notes: z.array(SkillExtractionSourceNoteInput).min(1).max(8),
  })
  .strict();

const TopicSourceInput = z
  .object({
    platform: Platform,
    title: NullableText,
    summary: NullableText,
    tags: z.array(z.string().trim().min(1).max(80)).max(50),
    body: z.string().max(4000).nullable(),
    source_url: z.string().url().nullable(),
  })
  .strict();

const TopicHotReferenceInput = z
  .object({
    note_id: z.string().trim().min(1).max(128),
    title: z.string().trim().min(1).max(120),
    summary: NullableText,
    author: NullableText,
    tags: z.array(z.string().trim().min(1).max(80)).max(50),
    engagement_total: z.number().int().nonnegative(),
    analysis_score: z.number().min(0).max(100).nullable(),
    source_url: z.string().url().nullable(),
  })
  .strict();

const TopicSkillCandidateInput = z
  .object({
    skill_id: z.string().trim().min(1).max(128),
    layer: z.number().int().min(1).max(5),
    name: z.string().trim().min(1).max(80),
    category: NullableText,
    tier_required: z.string().trim().min(1).max(32),
    summary: z.string().trim().min(1).max(512),
    source_type: z.string().trim().min(1).max(32),
    usage_count: z.number().int().nonnegative(),
    effectiveness_score: z.number().min(0).max(1).nullable(),
  })
  .strict();

const TopicRecommendInput = z
  .object({
    source: TopicSourceInput,
    account_diagnosis: JsonValue.nullable(),
    competitor_patterns: z.array(JsonValue).max(8),
    hot_refs: z.array(TopicHotReferenceInput).min(1).max(12),
    skills: z.array(TopicSkillCandidateInput).max(12),
  })
  .strict();

const DailyCompetitorSampleInput = z
  .object({
    note_id: z.string().trim().min(1).max(128),
    title: NullableText,
    summary: NullableText,
    author: NullableText,
    engagement: z.record(z.string(), JsonValue),
    insight: NullableText,
    score: z.number().min(0).max(100).nullable(),
    published_at: z.string().trim().min(1).nullable(),
    user_marked: z.boolean(),
  })
  .strict();

const DailyRecommendationInput = z
  .object({
    platform: Platform,
    competitor_samples: z.array(DailyCompetitorSampleInput).min(1).max(12),
    account_context: z
      .object({
        profile: JsonValue.nullable(),
        diagnosis: JsonValue.nullable(),
      })
      .strict(),
  })
  .strict();

const GenerationReferenceInput = z
  .object({
    title: NullableText,
    summary: NullableText,
    body: z.string().trim().min(1).max(12000),
    tags: z.array(z.string().trim().min(1).max(80)).max(50),
    author: NullableText,
    source_url: z.string().url().nullable(),
  })
  .strict();

const GenerationSkillInput = z
  .object({
    id: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(80),
    summary: z.string().trim().min(1).max(512),
    template: z.string().max(12000).nullable(),
    patterns: JsonValue.nullable(),
    notes: z.string().max(4000).nullable(),
  })
  .strict();

const GenerationBaseInput = {
  platform: Platform,
  topic: z.string().trim().min(1).max(200),
  brief: z.string().trim().min(1).max(2000).nullable(),
  account_profile: JsonValue.nullable(),
  generation_context: JsonValue.nullable(),
};

const AigcGenerationInput = z.discriminatedUnion('generation_mode', [
  z
    .object({
      ...GenerationBaseInput,
      generation_mode: z.literal('skill'),
      generation_directive: z.string().trim().min(1).max(1000),
      skill: GenerationSkillInput,
      reference: z.null(),
    })
    .strict(),
  z
    .object({
      ...GenerationBaseInput,
      generation_mode: z.literal('reference'),
      generation_directive: z.string().trim().min(1).max(1000),
      skill: z.null(),
      reference: GenerationReferenceInput,
    })
    .strict(),
  z
    .object({
      ...GenerationBaseInput,
      generation_mode: z.literal('reference_with_skill'),
      generation_directive: z.string().trim().min(1).max(1000),
      skill: GenerationSkillInput,
      reference: GenerationReferenceInput,
    })
    .strict(),
]);

const AigcRewriteInput = z
  .object({
    platform: Platform,
    base_title: z.string().trim().min(1).max(120),
    base_body: z.string().trim().min(1).max(30000),
    revision_instruction: z.string().trim().min(1).max(2000),
  })
  .strict();

const StrictNoteInsightOutput = z.object({ ...NoteInsightModelOutput.shape }).strict();
const StrictSelfFindingOutput = z
  .object({ ...SelfNoteAnalysisFindingModelOutput.shape })
  .strict();
const StrictSelfImprovementOutput = z
  .object({ ...SelfNoteAnalysisImprovementModelOutput.shape })
  .strict();
const StrictSelfContentFeatureOutput = z
  .object({ ...SelfNoteAnalysisContentFeaturesModelOutput.shape })
  .strict();
const StrictSelfNoteAnalysisOutput = z
  .object({
    ...StrictNoteInsightOutput.shape,
    strengths: z.array(StrictSelfFindingOutput).max(12),
    issues: z.array(StrictSelfFindingOutput).max(12),
    improvements: z.array(StrictSelfImprovementOutput).max(12),
    content_features: z.array(StrictSelfContentFeatureOutput).max(4),
    data_quality: z
      .object({
        body_available: z.boolean(),
        metrics_available: z.boolean(),
        metadata_available: z.boolean(),
        confidence: z.enum(['low', 'medium', 'high']),
      })
      .strict(),
    limitations: z.array(z.string().trim().min(1).max(240)).max(8),
  })
  .strict();

const StrictCompetitorHotspotOutput = z
  .object({
    structure: CompetitorHotspotPatternsModelOutput.shape.structure,
    hook_sentences: CompetitorHotspotPatternsModelOutput.shape.hook_sentences,
    publish_timing: CompetitorHotspotPatternsModelOutput.shape.publish_timing,
    tags: CompetitorHotspotPatternsModelOutput.shape.tags,
  })
  .strict();
const StrictCompetitorEngagementOutput = z
  .object({ ...CompetitorEngagementAttributionModelOutput.shape })
  .strict();
const StrictCompetitorInsightOutput = z
  .object({
    ...StrictNoteInsightOutput.shape,
    hotspot_patterns: StrictCompetitorHotspotOutput,
    engagement_attribution: StrictCompetitorEngagementOutput,
  })
  .strict();

export const PersonalStyleSkillModelOutput = z
  .object({
    name: z.string().trim().min(1).max(80),
    summary: z.string().trim().min(1).max(240),
    topic_preferences: z.array(z.string().trim().min(1).max(120)).min(1).max(16),
    structure_patterns: z.array(z.string().trim().min(1).max(160)).min(1).max(16),
    tone_traits: z.array(z.string().trim().min(1).max(120)).min(1).max(16),
    tag_habits: z.array(z.string().trim().min(1).max(80)).min(1).max(16),
    hook_patterns: z.array(z.string().trim().min(1).max(160)).min(1).max(16),
    generation_guidelines: z.array(z.string().trim().min(1).max(180)).min(1).max(16),
    avoid: z.array(z.string().trim().min(1).max(160)).min(1).max(16),
    confidence_score: z.coerce.number().min(0).max(100),
  })
  .strict();
export type PersonalStyleSkillModelOutput = z.infer<typeof PersonalStyleSkillModelOutput>;

const StrictTopicCompetitorReferenceOutput = z
  .object({ ...TopicCompetitorReferenceModelOutput.shape })
  .strict();
const StrictTopicSkillHintOutput = z.object({ ...TopicSkillHintModelOutput.shape }).strict();
const StrictTopicOpportunityOutput = z
  .object({
    ...TopicOpportunityModelOutput.shape,
    competitor_refs: z.array(StrictTopicCompetitorReferenceOutput).min(1).max(8),
    skill_hints: z.array(StrictTopicSkillHintOutput).min(1).max(5),
  })
  .strict();
const StrictTopicRecommendationOutput = z
  .object({
    ...TopicRecommendationModelOutput.shape,
    recommendations: z.array(StrictTopicOpportunityOutput).length(TOPIC_RECOMMENDATION_COUNT),
  })
  .strict();

const StrictDailyEvidenceOutput = z.object({ ...DailyRecommendationEvidence.shape }).strict();
const StrictDailyFitAssessmentOutput = z
  .object({ ...DailyRecommendationModelItemOutput.shape.fit_assessment.shape })
  .strict();
const StrictDailyItemOutput = z
  .object({
    ...DailyRecommendationModelItemOutput.shape,
    fit_assessment: StrictDailyFitAssessmentOutput,
    evidence: z.array(StrictDailyEvidenceOutput).min(1).max(8),
  })
  .strict();
const StrictDailyRecommendationOutput = z
  .object({
    items: z.array(StrictDailyItemOutput).length(3),
  })
  .strict();

const StrictGeneratedDraftOutput = z.object({ ...GeneratedDraftModelOutput.shape }).strict();

function jsonSchemaFor(schema: z.ZodType): Record<string, unknown> {
  const generated = z.toJSONSchema(schema, {
    target: 'draft-07',
    unrepresentable: 'any',
  }) as Record<string, unknown>;
  const { ['~standard']: _standard, ...jsonSchema } = generated;
  return jsonSchema;
}

function defineContract<TInputSchema extends z.ZodType, TOutputSchema extends z.ZodType>(
  definition: Omit<
    AiTaskContractDefinition<TInputSchema, TOutputSchema>,
    'inputContract' | 'outputContract'
  > & {
    inputContract: Omit<
      AiTaskContractDefinition<TInputSchema, TOutputSchema>['inputContract'],
      'jsonSchema'
    >;
    outputContract: Omit<
      AiTaskContractDefinition<TInputSchema, TOutputSchema>['outputContract'],
      'jsonSchema'
    >;
  },
): AiTaskContractDefinition<TInputSchema, TOutputSchema> {
  definition.inputContract.schema.parse(definition.inputContract.example);
  definition.outputContract.schema.parse(definition.outputContract.example);
  return {
    ...definition,
    inputContract: {
      ...definition.inputContract,
      jsonSchema: jsonSchemaFor(definition.inputContract.schema),
    },
    outputContract: {
      ...definition.outputContract,
      jsonSchema: jsonSchemaFor(definition.outputContract.schema),
    },
  };
}

const NOTE_EXAMPLE: z.input<typeof NoteModelInput> = {
  platform: 'xhs',
  account_id: 'account_1',
  author: { external_id: 'author_1', display_name: '示例作者' },
  content_type: 'normal',
  title: '示例标题',
  summary: '示例摘要',
  body: '示例正文',
  body_truncated: false,
  tags: ['示例'],
  engagement: { likes: 10, collects: 3 },
  media_count: 1,
  source_url: 'https://example.com/note/1',
  published_at: '2026-07-29T00:00:00Z',
};

const NOTE_OUTPUT_EXAMPLE: z.input<typeof StrictNoteInsightOutput> = {
  summary: '内容重点清晰。',
  tags: ['内容策略', '示例'],
  score: 82,
  originality: 76,
};

export const AI_TASK_CONTRACT_REGISTRY = {
  self_note_analysis: defineContract({
    taskType: 'self_note_analysis',
    featureKey: 'self_note_analysis',
    taskDisplayName: '本人单篇内容分析',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: { version: 'v1', schema: NoteModelInput, example: NOTE_EXAMPLE },
    outputContract: {
      version: 'v1',
      schema: StrictSelfNoteAnalysisOutput,
      example: {
        ...NOTE_OUTPUT_EXAMPLE,
        strengths: [
          {
            dimension_code: 'topic_selection',
            dimension_label: '选题表达',
            evidence: '标题与正文围绕同一主题展开。',
            impact: '读者能快速识别内容价值。',
            evidence_source: 'body',
          },
        ],
        issues: [],
        improvements: [
          {
            dimension_code: 'call_to_action',
            action: '补充明确行动指引。',
            reason: '正文目前缺少收束。',
            priority: 'medium',
            evidence_source: 'body',
          },
        ],
        content_features: [
          { feature_code: 'topic', value: '内容策略', evidence_source: 'metadata' },
          { feature_code: 'hook_style', value: '问题式开场', evidence_source: 'body' },
          { feature_code: 'structure', value: '问题—分析—建议', evidence_source: 'body' },
          { feature_code: 'cta_style', value: '邀请讨论', evidence_source: 'body' },
        ],
        data_quality: {
          body_available: true,
          metrics_available: true,
          metadata_available: true,
          confidence: 'high',
        },
        limitations: [],
      },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 8192, temperature: 0.3 },
  }),
  competitor_insight: defineContract({
    taskType: 'competitor_insight',
    featureKey: 'competitor_insight',
    taskDisplayName: '单篇竞品洞察',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: { version: 'v1', schema: NoteModelInput, example: NOTE_EXAMPLE },
    outputContract: {
      version: 'v1',
      schema: StrictCompetitorInsightOutput,
      example: {
        ...NOTE_OUTPUT_EXAMPLE,
        hotspot_patterns: {
          structure: ['痛点—方法—结果'],
          hook_sentences: ['先给出明确问题'],
          publish_timing: ['晚间发布'],
          tags: ['内容策略'],
        },
        engagement_attribution: {
          overall: '明确问题和可执行方法共同促进互动。',
          likes: ['价值表达直接'],
          collects: ['方法可复用'],
          comments: ['问题具有讨论空间'],
          shares: ['适合转发给同类创作者'],
        },
      },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 8192, temperature: 0.3 },
  }),
  skill_extraction: defineContract({
    taskType: 'skill_extraction',
    featureKey: 'skill_extraction',
    taskDisplayName: '个人风格提取',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: {
      version: 'v1',
      schema: SkillExtractionInput,
      example: {
        platform: 'xhs',
        account_id: 'account_1',
        source_notes: [
          {
            content_type: 'normal',
            media_count: 1,
            tags: ['内容策略'],
            engagement: { likes: 10 },
            analysis_score: 82,
            analysis_originality: 76,
            performance_score: 88,
            published_at: '2026-07-29T00:00:00Z',
            title: '示例标题',
            summary: '示例摘要',
            body_excerpt: '示例正文',
            body_truncated: false,
          },
        ],
      },
    },
    outputContract: {
      version: 'v1',
      schema: PersonalStyleSkillModelOutput,
      example: {
        name: '清晰拆解型表达',
        summary: '以问题开场，用分点方法给出可执行建议。',
        topic_preferences: ['内容策略'],
        structure_patterns: ['问题—拆解—行动'],
        tone_traits: ['直接', '克制'],
        tag_habits: ['使用少量高相关标签'],
        hook_patterns: ['用具体问题开场'],
        generation_guidelines: ['每段只表达一个重点'],
        avoid: ['复述来源原文'],
        confidence_score: 80,
      },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 2400, temperature: 0.3 },
  }),
  topic_recommend: defineContract({
    taskType: 'topic_recommend',
    featureKey: 'topic_recommend',
    taskDisplayName: '选题发现',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: {
      version: 'v1',
      schema: TopicRecommendInput,
      example: {
        source: {
          platform: 'xhs',
          title: '赛道种子',
          summary: '种子摘要',
          tags: ['内容策略'],
          body: '种子正文',
          source_url: 'https://example.com/topic/1',
        },
        account_diagnosis: { gaps: ['开头吸引力不足'] },
        competitor_patterns: [{ pattern: '清单型内容收藏率较高' }],
        hot_refs: [
          {
            note_id: 'note_1',
            title: '参考笔记',
            summary: '参考摘要',
            author: '示例作者',
            tags: ['内容策略'],
            engagement_total: 120,
            analysis_score: 88,
            source_url: 'https://example.com/note/1',
          },
        ],
        skills: [],
      },
    },
    outputContract: {
      version: 'v1',
      schema: StrictTopicRecommendationOutput,
      example: {
        ...NOTE_OUTPUT_EXAMPLE,
        recommendations: [0, 1, 2].map((index) => ({
          title: `用三步拆解提升开头吸引力 ${index + 1}`,
          angle: '从常见失败示例切入',
          why: '竞品证据与账号缺口同时支持该方向。',
          account_gap: '开头吸引力不足',
          priority_score: 90 - index,
          competitor_refs: [
            { note_id: 'note_1', title: '参考笔记', reason: '结构与选题相关' },
          ],
          skill_hints: [
            {
              skill_id: null,
              skill_name: '清单拆解',
              layer: 2 as const,
              prompt_hint: '采用三步结构',
            },
          ],
          generation_topic: `提升内容开头吸引力 ${index + 1}`,
          generation_brief: '用失败示例和三步方法展开。',
        })),
      },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 4000, temperature: 0.3 },
  }),
  daily_recommendation: defineContract({
    taskType: 'daily_recommendation',
    featureKey: 'daily_recommendation',
    taskDisplayName: '每日创作建议',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: {
      version: 'v1',
      schema: DailyRecommendationInput,
      example: {
        platform: 'xhs',
        competitor_samples: [
          {
            note_id: 'note_1',
            title: '参考笔记',
            summary: '参考摘要',
            author: '示例作者',
            engagement: { likes: 10 },
            insight: '清单结构表现较好',
            score: 88,
            published_at: '2026-07-29T00:00:00Z',
            user_marked: true,
          },
        ],
        account_context: { profile: null, diagnosis: null },
      },
    },
    outputContract: {
      version: 'v1',
      schema: StrictDailyRecommendationOutput,
      example: {
        items: [0, 1, 2].map((index) => ({
          title: `创作建议 ${index + 1}`,
          topic: '内容策略',
          angle: '从常见问题切入',
          reason: '竞品样本提供了明确证据。',
          fit_assessment: {
            summary: '当前缺少账号画像，仅按竞品证据判断。',
            basis: ['competitor_evidence' as const],
            confidence: 'low' as const,
          },
          generation_brief: '用三点清单展开。',
          evidence: [{ note_id: 'note_1', title: '参考笔记', reason: '结构相关' }],
        })),
      },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 3200, temperature: 0.3 },
  }),
  aigc_generation: defineContract({
    taskType: 'aigc_generation',
    featureKey: 'aigc_generation',
    taskDisplayName: '草稿生成',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: {
      version: 'v1',
      schema: AigcGenerationInput,
      example: {
        platform: 'xhs',
        topic: '内容策略',
        brief: '给新手的三步方法',
        account_profile: null,
        generation_context: null,
        generation_mode: 'skill',
        generation_directive: '仅使用 Skill 生成，不引用外部笔记。',
        skill: {
          id: 'skill_1',
          name: '清单拆解',
          summary: '用分点清单表达。',
          template: null,
          patterns: null,
          notes: null,
        },
        reference: null,
      },
    },
    outputContract: {
      version: 'v1',
      schema: StrictGeneratedDraftOutput,
      example: { title: '三步提升内容开头吸引力', body_md: '第一步：明确问题。' },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 4096, temperature: 0.7 },
  }),
  aigc_rewrite: defineContract({
    taskType: 'aigc_rewrite',
    featureKey: 'aigc_generation',
    taskDisplayName: '草稿改写',
    prompt: { required: true, platformStrategy: 'platform_then_default' },
    inputContract: {
      version: 'v1',
      schema: AigcRewriteInput,
      example: {
        platform: 'xhs',
        base_title: '原始标题',
        base_body: '原始正文',
        revision_instruction: '让开头更直接。',
      },
    },
    outputContract: {
      version: 'v1',
      schema: StrictGeneratedDraftOutput,
      example: { title: '改写后的标题', body_md: '更直接的开头。' },
    },
    modelRequirements: { structuredOutput: 'preferred', vision: false },
    inference: { maxTokens: 4096, temperature: 0.7 },
  }),
} as const satisfies Record<ModelAiTaskType, AiTaskContractDefinition>;

export const AI_TASK_CLASSIFICATIONS = {
  self_note_analysis: {
    kind: 'model',
    featureKey: 'self_note_analysis',
    promptRequired: true,
  },
  competitor_insight: {
    kind: 'model',
    featureKey: 'competitor_insight',
    promptRequired: true,
  },
  skill_extraction: { kind: 'model', featureKey: 'skill_extraction', promptRequired: true },
  topic_recommend: { kind: 'model', featureKey: 'topic_recommend', promptRequired: true },
  daily_recommendation: {
    kind: 'model',
    featureKey: 'daily_recommendation',
    promptRequired: true,
  },
  aigc_generation: { kind: 'model', featureKey: 'aigc_generation', promptRequired: true },
  aigc_rewrite: { kind: 'model', featureKey: 'aigc_generation', promptRequired: true },
  content_safety: { kind: 'deterministic', featureKey: null, promptRequired: false },
  video_transcription: { kind: 'placeholder', featureKey: null, promptRequired: false },
  video_scene_detect: { kind: 'placeholder', featureKey: null, promptRequired: false },
  video_ocr: { kind: 'placeholder', featureKey: null, promptRequired: false },
  video_summarize: { kind: 'placeholder', featureKey: null, promptRequired: false },
  cross_platform_compare: { kind: 'placeholder', featureKey: null, promptRequired: false },
} as const satisfies Record<AnalysisTaskType, AiTaskClassification>;

export function isModelAiTaskType(taskType: AnalysisTaskType): taskType is ModelAiTaskType {
  return AI_TASK_CLASSIFICATIONS[taskType].kind === 'model';
}

export function getAiTaskContract(taskType: AnalysisTaskType): AiTaskContractDefinition | null {
  return isModelAiTaskType(taskType) ? AI_TASK_CONTRACT_REGISTRY[taskType] : null;
}

export function requireAiTaskContract(taskType: string): AiTaskContractDefinition {
  if (!Object.hasOwn(AI_TASK_CONTRACT_REGISTRY, taskType)) {
    throw new Error(`AI task contract is not executable: ${taskType}`);
  }
  return AI_TASK_CONTRACT_REGISTRY[taskType as ModelAiTaskType];
}

export function getAiFeatureKeyForTask(taskType: string): AiFeatureKey | null {
  if (!Object.hasOwn(AI_TASK_CLASSIFICATIONS, taskType)) return null;
  return AI_TASK_CLASSIFICATIONS[taskType as AnalysisTaskType].featureKey;
}

export function listPromptTaskContracts(): readonly AiTaskContractDefinition[] {
  return MODEL_AI_TASK_TYPES.map((taskType) => AI_TASK_CONTRACT_REGISTRY[taskType]);
}

export interface AdminAiTaskContractProjection {
  task_type: ModelAiTaskType;
  task_display_name: string;
  feature_key: AiFeatureKey;
  prompt: AiTaskContractDefinition['prompt'];
  input_contract: {
    version: string;
    json_schema: Record<string, unknown>;
    example: unknown;
  };
  output_contract: {
    version: string;
    json_schema: Record<string, unknown>;
    example: unknown;
  };
  model_requirements: AiTaskContractDefinition['modelRequirements'];
}

export function projectAiTaskContract(
  contract: AiTaskContractDefinition,
): AdminAiTaskContractProjection {
  return {
    task_type: contract.taskType,
    task_display_name: contract.taskDisplayName,
    feature_key: contract.featureKey,
    prompt: contract.prompt,
    input_contract: {
      version: contract.inputContract.version,
      json_schema: contract.inputContract.jsonSchema,
      example: contract.inputContract.example,
    },
    output_contract: {
      version: contract.outputContract.version,
      json_schema: contract.outputContract.jsonSchema,
      example: contract.outputContract.example,
    },
    model_requirements: contract.modelRequirements,
  };
}
